import { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, Send, MessageCircle } from "lucide-react";
import api from "../api";
import { CONTACT, buildWhatsAppEnquiry } from "../constants";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, SectionHead } from "./Reveal";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  contact_method: "whatsapp",
  vehicle_id: "",
  vehicle_name: "",
  pickup_date: "",
  return_date: "",
  handover: "pickup",
  message: "",
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-gold/60 focus:bg-white/[0.05]";
const labelCls = "mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400";

const EnquiryForm = ({ preselected }) => {
  const { t, lang } = useLanguage();
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [waLink, setWaLink] = useState("");

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (preselected) {
      setForm((f) => ({ ...f, vehicle_id: preselected.id, vehicle_name: preselected.name }));
      setState("idle");
    }
  }, [preselected]);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = t("form.errRequired");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("form.errEmail");
    if (!form.phone.trim() || form.phone.trim().length < 5) e.phone = t("form.errRequired");
    if (!form.pickup_date) e.pickup_date = t("form.errRequired");
    if (!form.return_date) e.return_date = t("form.errRequired");
    else if (form.pickup_date && form.return_date < form.pickup_date) e.return_date = t("form.errDates");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const vehicle = vehicles.find((v) => v.id === form.vehicle_id);
    const link = buildWhatsAppEnquiry(form, vehicle ? vehicle.name : form.vehicle_name);
    setWaLink(link);
    window.open(link, "_blank", "noopener,noreferrer");
    setState("success");
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section id="enquiry" data-testid="enquiry-section" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
        <SectionHead eyebrow={t("form.eyebrow")} title={t("form.title")} sub={t("form.sub")} />

        <Reveal delay={0.1}>
          {state === "success" ? (
            <div data-testid="enquiry-success" className="flex h-full flex-col items-start justify-center rounded-3xl border border-emerald-400/25 bg-emerald-400/[0.06] p-10">
              <CheckCircle2 className="h-12 w-12 text-emerald-400" aria-hidden="true" />
              <p className="mt-5 text-lg font-medium leading-relaxed text-white">{t("form.success")}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  data-testid="enquiry-success-whatsapp"
                  href={waLink || CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-xs font-bold uppercase tracking-widest text-[#08080A] transition-all hover:brightness-110"
                >
                  <MessageCircle className="h-4 w-4" aria-hidden="true" />
                  {t("contact.whatsapp")}
                </a>
                <button
                  type="button"
                  data-testid="enquiry-again-button"
                  onClick={() => {
                    setForm(EMPTY);
                    setState("idle");
                  }}
                  className="rounded-full border border-white/15 px-6 py-3 text-xs font-bold uppercase tracking-widest text-slate-300 transition-colors hover:border-gold/50 hover:text-gold"
                >
                  {t("form.title")}
                </button>
              </div>
            </div>
          ) : (
            <form data-testid="enquiry-form" onSubmit={submit} noValidate className="gold-border-glow rounded-3xl border border-gold/15 bg-panel/80 p-6 backdrop-blur-md sm:p-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="enq-name" className={labelCls}>{t("form.name")} *</label>
                  <input id="enq-name" data-testid="enquiry-name-input" type="text" autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
                  {errors.name && <p data-testid="enquiry-error-name" className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="enq-email" className={labelCls}>{t("form.email")} *</label>
                  <input id="enq-email" data-testid="enquiry-email-input" type="email" autoComplete="email" dir="ltr" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                  {errors.email && <p data-testid="enquiry-error-email" className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="enq-phone" className={labelCls}>{t("form.phone")} *</label>
                  <input id="enq-phone" data-testid="enquiry-phone-input" type="tel" autoComplete="tel" dir="ltr" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
                  {errors.phone && <p data-testid="enquiry-error-phone" className="mt-1.5 text-xs text-red-400">{errors.phone}</p>}
                </div>
                <div>
                  <label htmlFor="enq-method" className={labelCls}>{t("form.method")}</label>
                  <select id="enq-method" data-testid="enquiry-method-select" value={form.contact_method} onChange={(e) => set("contact_method", e.target.value)} className={inputCls}>
                    <option value="whatsapp">{t("form.methodWhatsapp")}</option>
                    <option value="call">{t("form.methodCall")}</option>
                    <option value="email">{t("form.methodEmail")}</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enq-vehicle" className={labelCls}>{t("form.vehicle")}</label>
                  <select
                    id="enq-vehicle"
                    data-testid="enquiry-vehicle-select"
                    value={form.vehicle_id}
                    onChange={(e) => {
                      const v = vehicles.find((x) => x.id === e.target.value);
                      setForm((f) => ({ ...f, vehicle_id: e.target.value, vehicle_name: v ? v.name : "" }));
                    }}
                    className={inputCls}
                  >
                    <option value="">{t("form.vehicleAny")}</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="enq-pickup" className={labelCls}>{t("form.pickup")} *</label>
                  <input id="enq-pickup" data-testid="enquiry-pickup-date" type="date" min={today} value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} className={`${inputCls} [color-scheme:dark]`} />
                  {errors.pickup_date && <p data-testid="enquiry-error-pickup" className="mt-1.5 text-xs text-red-400">{errors.pickup_date}</p>}
                </div>
                <div>
                  <label htmlFor="enq-return" className={labelCls}>{t("form.return")} *</label>
                  <input id="enq-return" data-testid="enquiry-return-date" type="date" min={form.pickup_date || today} value={form.return_date} onChange={(e) => set("return_date", e.target.value)} className={`${inputCls} [color-scheme:dark]`} />
                  {errors.return_date && <p data-testid="enquiry-error-return" className="mt-1.5 text-xs text-red-400">{errors.return_date}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enq-handover" className={labelCls}>{t("form.handover")}</label>
                  <select id="enq-handover" data-testid="enquiry-handover-select" value={form.handover} onChange={(e) => set("handover", e.target.value)} className={inputCls}>
                    <option value="pickup">{t("form.handoverPickup")}</option>
                    <option value="delivery">{t("form.handoverDelivery")}</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enq-message" className={labelCls}>{t("form.message")}</label>
                  <textarea id="enq-message" data-testid="enquiry-message-input" rows={3} placeholder={t("form.messagePh")} value={form.message} onChange={(e) => set("message", e.target.value)} className={`${inputCls} resize-none`} />
                </div>
              </div>

              {state === "error" && (
                <p data-testid="enquiry-error" className="mt-5 flex items-center gap-2 rounded-xl border border-red-500/25 bg-red-500/[0.07] p-4 text-sm text-red-300">
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {t("form.error")}
                </p>
              )}

              <p className="mt-6 flex items-center gap-2 text-xs text-slate-500" data-testid="enquiry-whatsapp-note">
                <MessageCircle className="h-4 w-4 shrink-0 text-[#25D366]/70" aria-hidden="true" />
                {t("form.whatsappNote")}
              </p>

              <button
                type="submit"
                data-testid="enquiry-submit-button"
                disabled={state === "sending"}
                className="mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#08080A] transition-all hover:bg-gold-light hover:shadow-[0_0_40px_rgba(212,175,55,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {state === "sending" ? t("form.sending") : t("form.submit")}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default EnquiryForm;
